import React, { useState, useEffect } from 'react';
import apiFetch from '../../../utils/apiFetch';
import { SectionHead, Field, TextInput, CardSection, SaveBar } from '../components/ui';
import { C } from '../styles';
import { authHeaders } from '../helpers';

interface Props {
  businessCountry: string;
}

interface TaxData {
  business_number: string;
  payroll_rp_account: string;
  remitter_type: string;
}

interface Validation {
  ok: boolean;
  errors: Partial<Record<keyof TaxData, string>>;
}

interface Labels {
  sectionTitle: string;
  description: string;
  bnLabel: string;
  bnHelp: string;
  bnPh: string;
  rpLabel: string;
  rpHelp: string;
  rpPh: string;
}

interface RemittanceOption {
  value: string;
  label: string;
  desc: string;
}

const INITIAL: TaxData = { business_number: '', payroll_rp_account: '', remitter_type: 'monthly' };

const TAX_LABELS: Record<string, Labels> = {
  ca: { sectionTitle: 'CRA registration', description: 'Your CRA business identifiers and payroll remittance schedule.', bnLabel: 'Business Number (BN)', bnHelp: '9-digit number from your CRA registration.', bnPh: '123456789', rpLabel: 'Payroll account number', rpHelp: 'Usually RP0001. Found on your PD7A statement.', rpPh: 'RP0001' },
  us: { sectionTitle: 'IRS registration', description: 'Your federal and state employer identifiers.', bnLabel: 'Employer Identification Number (EIN)', bnHelp: '9-digit federal tax ID.', bnPh: '12-3456789', rpLabel: 'State employer ID', rpHelp: 'Your state payroll tax account.', rpPh: 'State ID' },
  gb: { sectionTitle: 'HMRC registration', description: 'Your PAYE employer references.', bnLabel: 'Company number', bnHelp: '8-digit number from Companies House.', bnPh: '12345678', rpLabel: 'PAYE reference', rpHelp: 'Your PAYE employer reference from HMRC.', rpPh: '123/AB456' },
  au: { sectionTitle: 'ATO registration', description: 'Your ABN and PAYG withholding registration.', bnLabel: 'Australian Business Number (ABN)', bnHelp: '11-digit number from the ABR.', bnPh: '12 345 678 901', rpLabel: 'PAYG withholding number', rpHelp: 'Your ATO withholding account number.', rpPh: 'PAYG number' },
  nz: { sectionTitle: 'IRD registration', description: 'Your NZ business and IRD identifiers.', bnLabel: 'NZ Business Number (NZBN)', bnHelp: '13-digit NZBN.', bnPh: '9429000000000', rpLabel: 'IRD number', rpHelp: 'Your IRD (Inland Revenue) number.', rpPh: '12-345-678' },
  sg: { sectionTitle: 'IRAS registration', description: 'Your Singapore employer identifiers.', bnLabel: 'Unique Entity Number (UEN)', bnHelp: 'Business registration number from ACRA.', bnPh: '12345678A', rpLabel: 'Tax reference number', rpHelp: 'Your IRAS employer tax reference.', rpPh: 'Tax ref' },
  jp: { sectionTitle: 'NTA registration', description: 'Your Japan corporate and tax office references.', bnLabel: 'Corporate number', bnHelp: '13-digit corporate number from Japan NTA.', bnPh: '1234567890123', rpLabel: 'Tax office reference', rpHelp: 'Your local tax office employer reference.', rpPh: 'Tax ref' },
  de: { sectionTitle: 'Finanzamt registration', description: 'Your German tax identifiers.', bnLabel: 'Umsatzsteuer-ID (VAT ID)', bnHelp: 'Your VAT identification number.', bnPh: 'DE123456789', rpLabel: 'Steuernummer', rpHelp: 'Your Finanzamt tax number.', rpPh: '12/345/67890' },
  fr: { sectionTitle: 'URSSAF registration', description: 'Your French business and social security identifiers.', bnLabel: 'SIRET number', bnHelp: '14-digit business identifier from INSEE.', bnPh: '12345678901234', rpLabel: 'URSSAF number', rpHelp: 'Your URSSAF employer contribution number.', rpPh: 'URSSAF ref' },
  za: { sectionTitle: 'SARS registration', description: 'Your SARS registration numbers.', bnLabel: 'Company registration number', bnHelp: 'Number from CIPC.', bnPh: '2020/123456/07', rpLabel: 'PAYE reference number', rpHelp: 'Your SARS PAYE reference.', rpPh: '7 followed by 9 digits' },
};

const DEFAULT_LABELS: Labels = {
  sectionTitle: 'Tax registration',
  description: 'Your business and payroll tax identifiers.',
  bnLabel: 'Business Number', bnHelp: 'Your tax registration number.', bnPh: '',
  rpLabel: 'Payroll account number', rpHelp: 'Your payroll tax account number.', rpPh: '',
};

const REMITTANCE_OPTIONS: Record<string, RemittanceOption[]> = {
  ca: [
    { value: 'quarterly', label: 'Quarterly', desc: '15th of the month after quarter end. For small remitters.' },
    { value: 'monthly', label: 'Monthly', desc: '15th of the following month. Most common.' },
    { value: 'accelerated_th1', label: 'Accelerated Threshold 1', desc: '25th of current month and 10th of next. For larger remitters.' },
    { value: 'accelerated_th2', label: 'Accelerated Threshold 2', desc: '3rd business day after each payday. Largest remitters.' },
  ],
  us: [
    { value: 'quarterly', label: 'Form 941 - Quarterly', desc: 'Filed each quarter with the IRS.' },
    { value: 'semiweekly', label: 'Semi-weekly', desc: 'Deposits due Wednesday or Friday depending on payday.' },
    { value: 'monthly', label: 'Monthly', desc: '15th of the following month. Most small employers.' },
  ],
  gb: [
    { value: 'monthly', label: 'Monthly RTI', desc: 'Real Time Information filed with each payroll.' },
    { value: 'quarterly', label: 'Quarterly', desc: 'For very small employers under HMRC thresholds.' },
  ],
  au: [
    { value: 'monthly', label: 'Monthly BAS', desc: 'For employers withholding more than $25K annually.' },
    { value: 'quarterly', label: 'Quarterly BAS', desc: 'Standard for most small businesses.' },
  ],
};

const DEFAULT_REMITTANCE: RemittanceOption[] = [
  { value: 'monthly', label: 'Monthly', desc: 'Send deductions to tax authority every month.' },
  { value: 'quarterly', label: 'Quarterly', desc: 'Send deductions every 3 months.' },
];

export default function TaxRegistrationSection({ businessCountry }: Props) {
  const [data, setData] = useState<TaxData>(INITIAL);
  const [original, setOriginal] = useState<TaxData>(INITIAL);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [validation, setValidation] = useState<Validation>({ ok: true, errors: {} });
  const [validated, setValidated] = useState<boolean>(false);
  const country = (businessCountry || 'ca').toLowerCase();

  useEffect(() => {
    apiFetch('/api/v1/company/profile', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d) {
          const initial: TaxData = {
            business_number: d.business_number || '',
            payroll_rp_account: d.payroll_rp_account || '',
            remitter_type: d.remitter_type || 'monthly',
          };
          setData(initial);
          setOriginal(initial);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const dirty = JSON.stringify(data) !== JSON.stringify(original);
  const set = <K extends keyof TaxData>(k: K, v: TaxData[K]): void => setData({ ...data, [k]: v });

  const validate = (): Validation => {
    const errors: Validation['errors'] = {};
    const bn = (data.business_number || '').replace(/\s|-/g, '');
    const rp = (data.payroll_rp_account || '').trim();

    if (country === 'ca') {
      if (bn && !/^\d{9}$/.test(bn)) errors.business_number = 'Business Number must be exactly 9 digits';
      if (rp && !/^RP\d{4}$/i.test(rp)) errors.payroll_rp_account = 'Payroll account must be format RP followed by 4 digits (e.g. RP0001)';
    } else if (country === 'us') {
      if (bn && !/^\d{9}$/.test(bn)) errors.business_number = 'EIN must be exactly 9 digits (12-3456789)';
    } else if (country === 'gb') {
      if (bn && !/^\d{8}$/.test(bn)) errors.business_number = 'Company number must be exactly 8 digits';
    } else if (country === 'au') {
      if (bn && !/^\d{11}$/.test(bn)) errors.business_number = 'ABN must be exactly 11 digits';
    } else if (country === 'nz') {
      if (bn && !/^\d{13}$/.test(bn)) errors.business_number = 'NZBN must be exactly 13 digits';
    } else if (country === 'jp') {
      if (bn && !/^\d{13}$/.test(bn)) errors.business_number = 'Corporate number must be exactly 13 digits';
    }
    return { ok: Object.keys(errors).length === 0, errors };
  };

  const onSave = async (): Promise<void> => {
    setValidated(true);
    const v = validate();
    setValidation(v);
    if (!v.ok) return;

    setSaving(true);
    try {
      const res = await apiFetch('/api/v1/company/profile', {
        method: 'PATCH', headers: authHeaders(), body: JSON.stringify(data),
      });
      if (res.ok) {
        const updated = await res.json();
        const refreshed: TaxData = {
          business_number: updated.business_number || '',
          payroll_rp_account: updated.payroll_rp_account || '',
          remitter_type: updated.remitter_type || 'monthly',
        };
        setData(refreshed);
        setOriginal(refreshed);
        setValidated(false);
        setValidation({ ok: true, errors: {} });
      } else {
        const err = await res.json();
        alert('Save failed: ' + JSON.stringify(err));
      }
    } catch (e: any) {
      alert('Save failed: ' + e.message);
    }
    setSaving(false);
  };

  const onDiscard = (): void => {
    setData(original);
    setValidated(false);
    setValidation({ ok: true, errors: {} });
  };

  if (loading) return <div style={{ color: C.muted, fontSize: 13 }}>Loading tax info...</div>;

  const labels: Labels = TAX_LABELS[country] || DEFAULT_LABELS;
  const remittanceOptions: RemittanceOption[] = REMITTANCE_OPTIONS[country] || DEFAULT_REMITTANCE;

  return (
    <>
      <SectionHead title="Tax registration" subtitle={labels.description} />

      {validated && !validation.ok && (
        <div style={{
          background: C.amberSoft, borderLeft: '3px solid ' + C.amber,
          borderRadius: '0 8px 8px 0', padding: '14px 18px', marginBottom: 18,
        }}>
          <div style={{ fontSize: 13, color: C.ink, lineHeight: 1.6 }}>
            <strong style={{ fontWeight: 700, display: 'block', marginBottom: 3 }}>Some fields need attention</strong>
            {Object.entries(validation.errors).map(([k, v]) => <div key={k}>{v}</div>)}
          </div>
        </div>
      )}

      <CardSection label={labels.sectionTitle}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Field
            label={labels.bnLabel}
            help={validated && validation.errors.business_number ? validation.errors.business_number : labels.bnHelp}
            error={validated ? validation.errors.business_number : undefined}
          >
            <TextInput
              value={data.business_number}
              onChange={(v) => set('business_number', v)}
              placeholder={labels.bnPh}
              error={validated ? validation.errors.business_number : undefined}
            />
          </Field>
          <Field
            label={labels.rpLabel}
            help={validated && validation.errors.payroll_rp_account ? validation.errors.payroll_rp_account : labels.rpHelp}
            error={validated ? validation.errors.payroll_rp_account : undefined}
          >
            <TextInput
              value={data.payroll_rp_account}
              onChange={(v) => set('payroll_rp_account', v)}
              placeholder={labels.rpPh}
              error={validated ? validation.errors.payroll_rp_account : undefined}
            />
          </Field>
        </div>
      </CardSection>

      <CardSection label="Remittance schedule">
        <div style={{ fontSize: 13, color: C.ink, marginBottom: 14, lineHeight: 1.6 }}>
          How often you send payroll deductions to the tax authority. Your remitter type is assigned by them based on your withholding amount.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {remittanceOptions.map((opt) => {
            const selected = data.remitter_type === opt.value;
            return (
              <div
                key={opt.value}
                onClick={() => set('remitter_type', opt.value)}
                style={{
                  background: '#fff',
                  border: '1.5px solid ' + (selected ? C.ink : C.line),
                  borderRadius: 10, padding: '14px 16px', cursor: 'pointer',
                  boxShadow: selected ? '0 0 0 3px rgba(10,26,30,.06)' : 'none',
                  display: 'flex', alignItems: 'flex-start', gap: 12,
                }}
              >
                <div style={{
                  width: 18, height: 18, borderRadius: '50%',
                  border: '1.5px solid ' + (selected ? C.ink : C.line),
                  background: selected ? C.ink : '#fff',
                  flex: '0 0 18px',
                  display: 'grid', placeItems: 'center', marginTop: 1,
                }}>
                  {selected && <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#fff' }} />}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.ink, marginBottom: 2 }}>{opt.label}</div>
                  <div style={{ fontSize: 12.5, color: C.ink }}>{opt.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
      </CardSection>

      <SaveBar dirty={dirty} saving={saving} onSave={onSave} onDiscard={onDiscard} label="Save tax registration" />
    </>
  );
}